import React, { useState, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  useTheme,
  CircularProgress,
  Alert,
  Divider,
  Button,
  IconButton,
  Tooltip,
  ButtonGroup
} from '@mui/material';
import {
  Compare as CompareIcon,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Info,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Refresh as ResetIcon,
  RestoreFromTrash as RestoreIcon,
  Visibility as ShowAllIcon,
  VisibilityOff as ShowConflictsIcon,
  FilterList as FilterIcon,
  Code as CodeIcon,
  TableChart as TableIcon
} from '@mui/icons-material';
import { CompareDataItem } from '../types/ImportExportTypes';
import CompareSummary from './CompareSummary';
import CompareDataTable from './CompareDataTable';
import JsonDiffViewer from './JsonDiffViewer';

interface ComparePreviewProps {
  compareData: CompareDataItem[] | null;
  loading: boolean;
  error: string | null;
  onAcceptChanges?: (editedData: CompareDataItem[]) => void;
  onRejectChanges?: () => void;
  onDataChange?: (index: number, field: string, value: any) => void;
  preservedData?: CompareDataItem[] | null; // Add preserved data prop
  isInAIAvatarLayout?: boolean;
}

export interface ComparePreviewRef {
  getEditedData: () => CompareDataItem[];
  getOriginalData: () => CompareDataItem[] | null;
  resetEditingStates: () => void;
}

const ComparePreview = forwardRef<ComparePreviewRef, ComparePreviewProps>(({
  compareData,
  loading,
  error,
  onAcceptChanges,
  onRejectChanges,
  onDataChange,
  preservedData,
  isInAIAvatarLayout = false
}, ref) => {
  const theme = useTheme();
  
  // Undo/Redo state management
  const [history, setHistory] = useState<CompareDataItem[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [currentData, setCurrentData] = useState<CompareDataItem[]>([]);
  
  // Field visibility state
  const [showOnlyConflicts, setShowOnlyConflicts] = useState(true);
  
  // View mode state (table or JSON)
  const [viewMode, setViewMode] = useState<'table' | 'json'>('table');
  
  // Calculate summary statistics first to determine initial filter
  const totalRecords = currentData?.length || 0;
  const newRecords = currentData?.filter(item => !item.current).length || 0;
  const existingRecords = currentData?.filter(item => item.current).length || 0;
  
  // Use proper deep comparison to detect conflicts
  const conflicts = currentData?.filter(item => {
    if (!item.current || !item.incoming) return false;
    
    // Get all fields from both objects
    const allFields = new Set<string>();
    Object.keys(item.current).forEach(key => allFields.add(key));
    Object.keys(item.incoming).forEach(key => allFields.add(key));
    
    // Check if any field is different
    for (const field of Array.from(allFields)) {
      const currentValue = item.current[field];
      const incomingValue = item.incoming[field];
      
      // Skip if both are undefined/null/empty (not a conflict)
      if ((currentValue === undefined || currentValue === null || currentValue === '') &&
          (incomingValue === undefined || incomingValue === null || incomingValue === '')) {
        continue;
      }
      
      // Use deep comparison - if any field is different, it's a conflict
      if (currentValue !== incomingValue) {
        // For objects/arrays, use JSON.stringify for comparison
        if (typeof currentValue === 'object' && typeof incomingValue === 'object') {
          try {
            if (JSON.stringify(currentValue) !== JSON.stringify(incomingValue)) {
              return true; // Found a conflict
            }
          } catch (e) {
            // If JSON.stringify fails, consider them different
            return true;
          }
        } else {
          return true; // Found a conflict
        }
      }
    }
    
    return false; // No conflicts found
  }).length || 0;

  // Determine initial active filter - prioritize incoming records first
  const getInitialFilter = (): 'current' | 'incoming' | 'new' | 'conflicted' => {
    if (totalRecords > 0) return 'incoming'; // Incoming records first priority
    if (existingRecords > 0) return 'current';
    if (newRecords > 0) return 'new';
    if (conflicts > 0) return 'conflicted';
    return 'incoming'; // fallback to incoming
  };

  const [activeFilter, setActiveFilter] = useState<'current' | 'incoming' | 'new' | 'conflicted'>(getInitialFilter());

  // Initialize current data and history when compareData or preservedData changes
  React.useEffect(() => {
    // Use preserved data if available, otherwise use compareData
    const dataToUse = preservedData && preservedData.length > 0 ? preservedData : compareData;
    
    if (dataToUse && dataToUse.length > 0) {
      setCurrentData(dataToUse);
      setHistory([dataToUse]);
      setHistoryIndex(0);
    } else {
      // Reset when no data is available
      setCurrentData([]);
      setHistory([]);
      setHistoryIndex(-1);
      setShowOnlyConflicts(true);
      setActiveFilter(getInitialFilter());
    }
  }, [compareData, preservedData]);

  // Reset editing states function
  const resetEditingStates = useCallback(() => {
    const dataToUse = preservedData && preservedData.length > 0 ? preservedData : compareData;
    
    if (dataToUse && dataToUse.length > 0) {
      setCurrentData(dataToUse);
      setHistory([dataToUse]);
      setHistoryIndex(0);
    } else {
      setCurrentData([]);
      setHistory([]);
      setHistoryIndex(-1);
    }
    setShowOnlyConflicts(true);
    setActiveFilter(getInitialFilter());
  }, [compareData, preservedData]);

  // Expose functions via ref
  useImperativeHandle(ref, () => ({
    getEditedData: () => currentData,
    getOriginalData: () => compareData, // Return the original compare data
    resetEditingStates
  }), [currentData, compareData, resetEditingStates]);

  // Add to history when data changes
  const addToHistory = useCallback((newData: CompareDataItem[]) => {
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(newData);
      return newHistory;
    });
    setHistoryIndex(prev => prev + 1);
  }, [historyIndex]);

  // Undo function
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setCurrentData(history[newIndex]);
    }
  }, [historyIndex, history]);

  // Redo function
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setCurrentData(history[newIndex]);
    }
  }, [historyIndex, history]);

  // Reset function
  const handleReset = useCallback(() => {
    if (compareData && compareData.length > 0) {
      setCurrentData(compareData);
      setHistory([compareData]);
      setHistoryIndex(0);
    }
  }, [compareData]);

  // Handle data change with history tracking
  const handleDataChange = useCallback((index: number, field: string, value: any) => {
    
    const newData = [...currentData];
    if (newData[index]) {
      // Ensure incoming object exists
      if (!newData[index].incoming) {
        newData[index].incoming = {};
      }
      
      newData[index] = {
        ...newData[index],
        incoming: {
          ...newData[index].incoming,
          [field]: value
        }
      };
    }
    
    
    setCurrentData(newData);
    addToHistory(newData);
    onDataChange?.(index, field, value);
    
  }, [currentData, addToHistory, onDataChange]);

  // Function to filter fields based on conflicts
  const filterFieldsByConflicts = useCallback((data: CompareDataItem[]): CompareDataItem[] => {
    if (!showOnlyConflicts) return data;
    
    return data.filter(item => {
      if (!item.current || !item.incoming) return true; // Keep items without current/incoming
      
      const conflictingFields: Record<string, any> = {};
      const currentFields = item.current;
      const incomingFields = item.incoming;
      
      // Find fields that have different values (actual conflicts)
      Object.keys(currentFields).forEach(field => {
        const currentValue = currentFields[field];
        const incomingValue = incomingFields[field];
        
        // Only include if both values exist and are different
        if (currentValue !== undefined && incomingValue !== undefined && 
            currentValue !== null && incomingValue !== null &&
            JSON.stringify(currentValue) !== JSON.stringify(incomingValue)) {
          conflictingFields[field] = incomingValue;
        }
      });
      
      // Also check for fields that exist in incoming but not in current (new fields)
      Object.keys(incomingFields).forEach(field => {
        if (!(field in currentFields) && 
            incomingFields[field] !== undefined && 
            incomingFields[field] !== null &&
            incomingFields[field] !== '') {
          conflictingFields[field] = incomingFields[field];
        }
      });
      
      // Only keep the item if there are actual conflicting fields
      return Object.keys(conflictingFields).length > 0;
    }).map(item => ({
      ...item,
      _showOnlyConflicts: true,
      _conflictingFields: item.current && item.incoming ? (() => {
        const conflictingFields: Record<string, any> = {};
        const currentFields = item.current;
        const incomingFields = item.incoming;
        
        // Find fields that have different values (actual conflicts)
        Object.keys(currentFields).forEach(field => {
          const currentValue = currentFields[field];
          const incomingValue = incomingFields[field];
          
          if (currentValue !== undefined && incomingValue !== undefined && 
              currentValue !== null && incomingValue !== null &&
              JSON.stringify(currentValue) !== JSON.stringify(incomingValue)) {
            conflictingFields[field] = incomingValue;
          }
        });
        
        // Also check for fields that exist in incoming but not in current (new fields)
        Object.keys(incomingFields).forEach(field => {
          if (!(field in currentFields) && 
              incomingFields[field] !== undefined && 
              incomingFields[field] !== null &&
              incomingFields[field] !== '') {
            conflictingFields[field] = incomingFields[field];
          }
        });
        
        return conflictingFields;
      })() : {}
    }));
  }, [showOnlyConflicts]);

  // Keyboard shortcuts for undo/redo
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey) {
        if (event.key === 'z' && !event.shiftKey) {
          event.preventDefault();
          handleUndo();
        } else if (event.key === 'y' || (event.key === 'z' && event.shiftKey)) {
          event.preventDefault();
          handleRedo();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  const summary = {
    totalRecords,
    newRecords,
    existingRecords,
    conflicts
  };

  // Filter data based on active filter and field visibility
  const filteredData = useMemo(() => {
    if (!currentData || currentData.length === 0) return [];
    
    let filteredByType;
    switch (activeFilter) {
      case 'current':
        filteredByType = currentData.filter(item => item.current);
        break;
      case 'incoming':
        filteredByType = currentData.filter(item => item.incoming);
        break;
      case 'new':
        filteredByType = currentData.filter(item => !item.current);
        break;
      case 'conflicted':
        filteredByType = currentData.filter(item => 
          item.current && item.incoming && 
          JSON.stringify(item.current) !== JSON.stringify(item.incoming)
        );
        break;
      default:
        filteredByType = currentData;
    }
    
    // Apply field filtering based on conflicts
    return filterFieldsByConflicts(filteredByType);
  }, [currentData, activeFilter, filterFieldsByConflicts]);

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Comparing Data...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we compare your data with existing records.
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Comparison Failed
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (!currentData || currentData.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CompareIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          No Data to Compare
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upload a file to see comparison results.
        </Typography>
      </Box>
    );
  }

  const handleFilterChange = (filterType: 'current' | 'incoming' | 'new' | 'conflicted') => {
    // Only allow filter change if there are records for that type
    let hasRecords = false;
    switch (filterType) {
      case 'current':
        hasRecords = summary.existingRecords > 0;
        break;
      case 'incoming':
        hasRecords = summary.totalRecords > 0;
        break;
      case 'new':
        hasRecords = summary.newRecords > 0;
        break;
      case 'conflicted':
        hasRecords = summary.conflicts > 0;
        break;
    }
    
    if (hasRecords) {
      setActiveFilter(filterType);
    }
  };

  return (
    <Box sx={{ p: 2.5 }}>
      <Typography 
        variant="h6" 
        gutterBottom 
        sx={{ 
          color: isInAIAvatarLayout
            ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
            : '#0F2A52',
          fontWeight: 600,
          fontSize: '1.1rem',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1
        }}
      >
        <CompareIcon />
        Data Comparison Results
      </Typography>
      
      <Typography 
        variant="body2" 
        color="text.secondary" 
        sx={{ 
          mb: 3,
          fontSize: '0.85rem',
          lineHeight: 1.5
        }}
      >
        Review the differences between your imported data and existing records.
      </Typography>

      {/* Summary Cards */}
      <CompareSummary 
        summary={summary} 
        onFilterChange={handleFilterChange}
        activeFilter={activeFilter}
        isInAIAvatarLayout={isInAIAvatarLayout}
      />

      <Divider sx={{ my: 3 }} />

      {/* Data Comparison Table */}
      <Card 
        elevation={0}
        sx={{
          borderRadius: 1.5,
          boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
          border: '1px solid rgba(15, 40, 76, 0.08)',
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2
          }}>
            <Box>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{
                  color: isInAIAvatarLayout
                    ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
                    : '#0F2A52',
                  fontWeight: 600,
                  fontSize: '1rem',
                  mb: 0.5
                }}
              >
                Detailed Comparison
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  fontSize: '0.85rem',
                  lineHeight: 1.5
                }}
              >
                Compare current and incoming data side by side.
                {showOnlyConflicts && (
                  <Typography 
                    component="span" 
                    variant="caption" 
                    sx={{ 
                      ml: 1,
                      color: isInAIAvatarLayout
                        ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
                        : '#ed6c02',
                      fontWeight: 500
                    }}
                  >
                    (Showing only conflicting fields)
                  </Typography>
                )}
              </Typography>
            </Box>
            
            {/* Action Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* View Mode Toggle */}
              <Tooltip title={viewMode === 'table' ? "Switch to JSON View" : "Switch to Table View"}>
                <IconButton
                  onClick={() => setViewMode(viewMode === 'table' ? 'json' : 'table')}
                  size="small"
                  sx={{
                    border: `1px solid ${isInAIAvatarLayout ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff') : '#0F2A52'}`,
                    color: viewMode === 'json' ? (isInAIAvatarLayout ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff') : '#0F2A52') : '#666',
                    backgroundColor: viewMode === 'json' ? (isInAIAvatarLayout ? (theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)') : 'rgba(15, 42, 82, 0.08)') : 'transparent',
                    '&:hover': {
                      borderColor: isInAIAvatarLayout ? (theme.palette.mode === 'light' ? '#333333' : '#e0e0e0') : '#0A1F3F',
                      backgroundColor: viewMode === 'json' ? (isInAIAvatarLayout ? (theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.15)') : 'rgba(15, 42, 82, 0.12)') : (isInAIAvatarLayout ? (theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.1)') : 'rgba(15, 42, 82, 0.04)'),
                    },
                    mr: 1
                  }}
                >
                  {viewMode === 'table' ? <CodeIcon /> : <TableIcon />}
                </IconButton>
              </Tooltip>
              
              {/* Field Visibility Toggle */}
              <Tooltip title={showOnlyConflicts ? "Show All Fields" : "Show Only Conflicting Fields"}>
                <IconButton
                  onClick={() => setShowOnlyConflicts(!showOnlyConflicts)}
                  size="small"
                  sx={{
                    border: `1px solid ${isInAIAvatarLayout ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff') : '#0F2A52'}`,
                    color: showOnlyConflicts ? (isInAIAvatarLayout ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff') : '#0F2A52') : '#666',
                    backgroundColor: showOnlyConflicts ? (isInAIAvatarLayout ? (theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)') : 'rgba(15, 42, 82, 0.08)') : 'transparent',
                    '&:hover': {
                      borderColor: isInAIAvatarLayout ? (theme.palette.mode === 'light' ? '#333333' : '#e0e0e0') : '#0A1F3F',
                      backgroundColor: showOnlyConflicts ? (isInAIAvatarLayout ? (theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.15)') : 'rgba(15, 42, 82, 0.12)') : (isInAIAvatarLayout ? (theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.1)') : 'rgba(15, 42, 82, 0.04)'),
                    },
                    mr: 1
                  }}
                >
                  {showOnlyConflicts ? <ShowConflictsIcon /> : <ShowAllIcon />}
                </IconButton>
              </Tooltip>
              
              {/* History Status */}
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  fontSize: '0.75rem',
                  mr: 1
                }}
              >
                {historyIndex + 1} / {history.length}
              </Typography>
              
              <ButtonGroup 
                variant="outlined" 
                size="small"
                sx={{
                  '& .MuiButton-root': {
                    borderColor: isInAIAvatarLayout ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff') : '#0F2A52',
                    color: isInAIAvatarLayout ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff') : '#0F2A52',
                    '&:hover': {
                      borderColor: isInAIAvatarLayout ? (theme.palette.mode === 'light' ? '#333333' : '#e0e0e0') : '#0A1F3F',
                      backgroundColor: isInAIAvatarLayout ? (theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.1)') : 'rgba(15, 42, 82, 0.04)',
                    },
                    '&:disabled': {
                      borderColor: 'rgba(15, 42, 82, 0.3)',
                      color: 'rgba(15, 42, 82, 0.3)',
                    }
                  }
                }}
              >
                <Tooltip title="Undo (Ctrl+Z)">
                  <IconButton
                    onClick={handleUndo}
                    disabled={historyIndex <= 0}
                    size="small"
                  >
                    <UndoIcon />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Redo (Ctrl+Y)">
                  <IconButton
                    onClick={handleRedo}
                    disabled={historyIndex >= history.length - 1}
                    size="small"
                  >
                    <RedoIcon />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Reset to Original">
                  <IconButton
                    onClick={handleReset}
                    disabled={historyIndex === 0}
                    size="small"
                  >
                    <ResetIcon />
                  </IconButton>
                </Tooltip>
              </ButtonGroup>
            </Box>
          </Box>
          
          {viewMode === 'table' ? (
            <CompareDataTable 
              data={filteredData}
              onAcceptChanges={() => {
                onAcceptChanges?.(currentData);
              }}
              onRejectChanges={onRejectChanges}
              onDataChange={(filteredIndex, field, value) => {
                // Find the actual index in currentData by matching the item
                const filteredItem = filteredData[filteredIndex];
                if (!filteredItem) return;
                
                const actualIndex = currentData.findIndex(originalItem => {
                  // Try to match by _id if available
                  if (originalItem.current?._id && filteredItem.current?._id) {
                    return originalItem.current._id === filteredItem.current._id;
                  }
                  if (originalItem.incoming?._id && filteredItem.incoming?._id) {
                    return originalItem.incoming._id === filteredItem.incoming._id;
                  }
                  // Fallback: match by comparing the objects
                  return JSON.stringify(originalItem.current) === JSON.stringify(filteredItem.current) &&
                         JSON.stringify(originalItem.incoming) === JSON.stringify(filteredItem.incoming);
                });
                
                // Use actualIndex if found, otherwise use filteredIndex as fallback
                const recordIndex = actualIndex !== -1 ? actualIndex : filteredIndex;
                handleDataChange(recordIndex, field, value);
              }}
              showOnlyConflicts={showOnlyConflicts}
              isInAIAvatarLayout={isInAIAvatarLayout}
            />
          ) : (
            <Box sx={{ 
              maxHeight: 600, 
              overflow: 'auto',
              backgroundColor: '#ffffff',
              border: '1px solid #d0d7de',
              borderRadius: '6px',
              p: 0
            }}>
              {/* JSON View Header */}
              <Box sx={{ 
                backgroundColor: '#f6f8fa',
                padding: '12px 16px',
                borderBottom: '1px solid #d0d7de',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderTopLeftRadius: '6px',
                borderTopRightRadius: '6px'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ 
                    width: '16px',
                    height: '16px',
                    backgroundColor: isInAIAvatarLayout ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff') : '#0F2A52',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Typography sx={{ 
                      color: isInAIAvatarLayout 
                        ? (theme.palette.mode === 'light' ? '#ffffff' : '#000000')
                        : 'white', 
                      fontSize: '0.7rem', 
                      fontWeight: 'bold' 
                    }}>
                      J
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#24292e', 
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}
                  >
                    JSON View
                  </Typography>
                </Box>
                <Box sx={{ 
                  fontSize: '0.75rem',
                  color: '#656d76',
                  backgroundColor: '#ffffff',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  border: '1px solid #d0d7de'
                }}>
                  {filteredData.length} records
                </Box>
              </Box>
              
              {/* JSON Diff Content */}
              <Box sx={{ p: 0 }}>
                {filteredData.map((item, filteredIndex) => {
                  // Find the actual index in currentData by matching the item
                  const actualIndex = currentData.findIndex(originalItem => {
                    // Try to match by _id if available
                    if (originalItem.current?._id && item.current?._id) {
                      return originalItem.current._id === item.current._id;
                    }
                    if (originalItem.incoming?._id && item.incoming?._id) {
                      return originalItem.incoming._id === item.incoming._id;
                    }
                    // Fallback: match by comparing the objects
                    return JSON.stringify(originalItem.current) === JSON.stringify(item.current) &&
                           JSON.stringify(originalItem.incoming) === JSON.stringify(item.incoming);
                  });
                  
                  // Use actualIndex if found, otherwise use filteredIndex as fallback
                  const recordIndex = actualIndex !== -1 ? actualIndex : filteredIndex;
                  
                  return (
                    <JsonDiffViewer
                      key={recordIndex}
                      currentData={item.current}
                      incomingData={item.incoming}
                      recordIndex={filteredIndex}
                      recordType="Record"
                      editable={true}
                      onDataChange={(field, value) => handleDataChange(recordIndex, field, value)}
                      showOnlyConflicts={showOnlyConflicts}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
});

export default ComparePreview;
